import {hasOpenedAsAntibody} from 'antibodies/hooks/useAntibodyUIState';
import {assert} from 'Asserts';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FolderSelectorModalClass from 'file-browser/FolderSelectorModal';
import BaseFolderItem, {BaseFolderItemAttributes} from 'folder-items/BaseFolderItem';
import {getInsufficientPermissionsToEditMessage} from 'folder-items/FileUtil';
import {isComplexPolymer} from 'modbio/complex-polymers/ComplexPolymerUtil';

export interface BasicFolderItemAttributes extends BaseFolderItemAttributes {
  isLatest: boolean;
}

interface BasicFolderItem {
  get<K extends keyof BasicFolderItemAttributes>(key: K): BasicFolderItemAttributes[K];
  set<K extends keyof BasicFolderItemAttributes>(
    attributeName: K,
    value: BasicFolderItemAttributes[K],
    options?: Backbone.ModelSetOptions
  ): BasicFolderItem;
  set(obj: Partial<BasicFolderItemAttributes>, options?: Backbone.ModelSetOptions): BasicFolderItem;
}

class BasicFolderItem extends BaseFolderItem {
  override urlRoot = '/1/api/basic-folder-items';

  initialize(): void {
    this.registerListeners();
    this.set({isLatest: true});
  }

  registerListeners(): void {
    // TODO: add `@on 'change', @updateStatusBar` once updateStatusBar is defined
  }

  cleanUp(): void {
    this.stopListening();
  }

  duplicate(): void {
    FolderSelectorModalClass.openForCopy(
      {
        showInventory: true,
        showNotebook: false,
      },
      (folderId: string) => {
        FileBrowserActions.copyAndOpenItem(this.id, folderId);
      }
    );
  }

  isLatest(): boolean {
    return this.get('isLatest');
  }

  setIsLatest(isLatest: boolean): void {
    assert(
      isComplexPolymer(this.id) || hasOpenedAsAntibody(this.id),
      'Basic folder item must be complex polymer or antibody to set version attribute'
    );
    this.set({isLatest});
  }

  override allowEditsToNote(): boolean {
    return this.isLatest() && super.allowEditsToNote();
  }

  getBannerText(): string | null {
    if (!this.isLatest()) {
      return 'Editing disabled on previous versions.';
    } else if (this.isArchived()) {
      return 'Editing disabled on archived entity.';
    }
    return getInsufficientPermissionsToEditMessage(this.permissions(), 'entity');
  }

  override setBanner(): void {
    if (!this.writable() || !this.isLatest()) {
      this.setBannerText(this.getBannerText());
    } else {
      this.setBannerText(null);
    }
  }
}

export default BasicFolderItem;
